import { useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Textarea } from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import type { ConsentRequirement, ConsentStatus, ConsentTypeId } from './types';

/**
 * The consent screen, shared by the Registration step (RegisterStage) and the
 * Patient Records consent panel so the two cannot show the patient different
 * wording or disagree about what is on file.
 *
 * Controlled: the caller owns the answers. Registration collects all of them
 * and submits once; Patient Records saves each one as it is answered. Neither
 * behaviour belongs in here.
 */

export type ConsentAnswerValue = 'accept' | 'decline';

export interface ConsentCapturePanelProps {
  items: ConsentRequirement[];
  /** consent_type -> the staff member's current selection for it. */
  answers: Partial<Record<ConsentTypeId, ConsentAnswerValue>>;
  onAnswer: (type: ConsentTypeId, value: ConsentAnswerValue) => void;
  notes?: Partial<Record<ConsentTypeId, string>>;
  onNotesChange?: (type: ConsentTypeId, notes: string) => void;
  /**
   * Hide the types that already have a valid answer on file behind a summary
   * line instead of asking again. This is the "once per patient, not every
   * visit" rule made visible — Registration passes true, the Patient Records
   * panel passes false because reviewing them is the whole point there.
   */
  collapseSettled?: boolean;
  disabled?: boolean;
  /** Per-type save state, for the Patient Records panel's one-at-a-time saves. */
  savingType?: ConsentTypeId | null;
}

const STATUS_BADGE: Record<ConsentStatus, { variant: 'success' | 'warn' | 'danger' | 'neutral'; label: string }> = {
  granted: { variant: 'success', label: 'Accepted' },
  declined: { variant: 'warn', label: 'Declined' },
  expired: { variant: 'danger', label: 'Expired — ask again' },
  missing: { variant: 'neutral', label: 'Not recorded' },
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function validityLabel(days: number | null | undefined): string {
  if (!days) return 'Does not expire';
  if (days % 365 === 0) return `Valid for ${days / 365} year${days === 365 ? '' : 's'}`;
  if (days % 30 === 0) return `Valid for ${days / 30} month${days === 30 ? '' : 's'}`;
  return `Valid for ${days} days`;
}

export function ConsentStatusBadge({ status }: { status: ConsentStatus }) {
  const s = STATUS_BADGE[status];
  return <Badge variant={s.variant}>{s.label}</Badge>;
}

function ConsentCard({
  item, answer, onAnswer, notes, onNotesChange, collapsed, disabled, saving,
}: {
  item: ConsentRequirement;
  answer: ConsentAnswerValue | undefined;
  onAnswer: (value: ConsentAnswerValue) => void;
  notes: string | undefined;
  onNotesChange?: (notes: string) => void;
  collapsed: boolean;
  disabled: boolean;
  saving: boolean;
}) {
  const [expanded, setExpanded] = useState(!collapsed);
  const showForm = expanded || !collapsed;

  // A required consent that has been explicitly declined is the one state this
  // screen has to shout about: it is what stops Registration completing.
  const blocking = item.required && answer === 'decline';

  return (
    <div
      className={cn(
        'rounded-xl border bg-card p-4',
        blocking ? 'border-danger' : 'border-border'
      )}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="text-[14px] font-semibold">{item.template?.title || item.label}</div>
        <Badge variant={item.required ? 'danger' : 'neutral'}>{item.required ? 'Mandatory' : 'Optional'}</Badge>
        <ConsentStatusBadge status={item.status} />
        {item.template && (
          <span className="ml-auto font-mono text-[10.5px] text-muted-foreground">
            v{item.template.version} · {validityLabel(item.template.validity_days)}
          </span>
        )}
      </div>

      <div className="mb-3 text-[12.5px] text-ink-muted">{item.description}</div>

      {item.current && (
        <div className="mb-3 rounded-lg bg-muted px-3 py-2 text-[12px] text-ink-muted">
          {item.current.granted ? 'Accepted' : 'Declined'} on {formatDate(item.current.recorded_at)}
          {item.current.granted_by ? ` by ${item.current.granted_by}` : ''}
          {item.current.version_on_file ? ` (version ${item.current.version_on_file})` : ''}
          {item.current.expires_at ? ` · expires ${formatDate(item.current.expires_at)}` : ''}
          {collapsed && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="ml-2 text-teal underline-offset-2 hover:underline"
            >
              {expanded ? 'Hide' : 'Record again'}
            </button>
          )}
        </div>
      )}

      {showForm && (
        <>
          {item.template ? (
            <div className="mb-3 max-h-56 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-background p-3 text-[12.5px] leading-relaxed">
              {item.template.body}
            </div>
          ) : (
            // Every version retired by an Admin. The answer is still worth
            // recording — it just carries no snapshot, exactly like the
            // pre-existing AI consents captured before wording existed.
            <div className="mb-3 rounded-lg border border-warn bg-warn-soft p-3 text-[12.5px] text-warn-ink">
              No consent wording is published for this consent yet. An administrator can publish it in
              Admin Panel → Clinical Setup → Consent Forms. Recording an answer now will not capture any text.
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <SegmentedControl<ConsentAnswerValue>
              value={answer ?? null}
              onChange={onAnswer}
              disabled={disabled || saving}
              options={[
                { value: 'accept', label: 'Accept', tone: 'success' },
                { value: 'decline', label: 'Decline', tone: item.required ? 'danger' : 'warn' },
              ]}
            />
            {saving && <span className="text-[12px] text-ink-muted">Saving…</span>}
          </div>

          {blocking && (
            <div className="mt-2.5 text-[12.5px] font-medium text-danger">
              Registration cannot be completed while {item.label} is declined. The patient must accept it to proceed.
            </div>
          )}
          {!item.required && answer === 'decline' && (
            <div className="mt-2.5 text-[12.5px] text-ink-muted">
              Recorded as declined. This does not affect registration or treatment.
            </div>
          )}

          {onNotesChange && (
            <div className="mt-3">
              <Textarea
                rows={2}
                value={notes || ''}
                onChange={(e) => onNotesChange(e.target.value)}
                placeholder="Notes (optional) — e.g. consent given by guardian, interpreter used"
                disabled={disabled || saving}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function ConsentCapturePanel({
  items, answers, onAnswer, notes, onNotesChange, collapseSettled = false, disabled = false, savingType = null,
}: ConsentCapturePanelProps) {
  if (!items.length) {
    return <div className="text-[13px] text-ink-muted">No consent types are configured.</div>;
  }
  return (
    <div className="flex flex-col gap-3">
      {items.map((item) => (
        <ConsentCard
          key={item.consent_type}
          item={item}
          answer={answers[item.consent_type]}
          onAnswer={(value) => onAnswer(item.consent_type, value)}
          notes={notes?.[item.consent_type]}
          onNotesChange={onNotesChange ? (v) => onNotesChange(item.consent_type, v) : undefined}
          // Settled = a live answer that nothing is asking to revisit. Asking
          // again would be exactly the per-visit re-consenting this whole
          // feature exists to avoid.
          collapsed={collapseSettled && !item.due}
          disabled={disabled}
          saving={savingType === item.consent_type}
        />
      ))}
    </div>
  );
}
